import update, {Query} from 'immutability-helper';
import * as _ from 'lodash';
import {RunKeySection} from './runs';
import * as Run from './runs';
import * as Obj from './obj';

export type Filter = IndividualFilter | GroupFilter;

export const EMPTY_FILTER_VALUE = null;

// needs to match IndividualFilter.op
// TODO: using enum may allow us to get rid of the duplication.
const valueOps = ['=', '!=', '<', '>', '<=', '>=', 'REGEX'];
export type ValueOp = '=' | '!=' | '<' | '>' | '<=' | '>=' | 'REGEX' | 'EXISTS';

export type TimeOption = 'hours' | 'minutes' | 'seconds';

export interface FilterMeta {
  unit: TimeOption;
}

export interface ValueFilter {
  readonly op: ValueOp;
  readonly key: Run.Key;
  readonly value: Run.Value;
  // This is metadata for the client the server won't read.
  readonly meta?: FilterMeta;
}

const multiValueOps = ['IN', 'NIN'];
export type MultiValueOp = 'IN' | 'NIN';
export interface MultiValueFilter {
  readonly op: MultiValueOp;
  readonly key: Run.Key;
  readonly value: Run.Value[];
}

// These operators don't allow user additions when creating filters
const noAdditionOps = ['IN', 'NIN', '='];

export interface ToggleableFilter {
  disabled?: boolean;
}

export interface DefaultToggleFilter {
  filter: IndividualFilter;
  label: string;
}

const ops = valueOps.concat(multiValueOps);
export type IndividualOp = ValueOp | MultiValueOp;
export type IndividualFilter = (ValueFilter | MultiValueFilter) &
  ToggleableFilter;

export interface GroupFilter {
  readonly op: 'AND' | 'OR';
  readonly filters: Filter[];
  readonly disabled?: boolean;
}

export interface AndFilter extends GroupFilter {
  readonly op: 'AND';
  filters: Filter[];
}

export interface OrFilter extends GroupFilter {
  readonly op: 'OR';
  filters: Filter[];
}

// Run name is always not equal to null. selectionmanager uses this to get
// a true value
export const TRUE_FILTER: IndividualFilter = {
  key: {
    section: 'run',
    name: 'name',
  },
  op: '!=',
  value: null,
};

// Run name never equals null. selectionmanager uses this to get a
// false value
export const FALSE_FILTER: IndividualFilter = {
  key: {
    section: 'run',
    name: 'name',
  },
  op: '=',
  value: null,
};
export function isGroup(filter: Filter): filter is GroupFilter {
  return (filter as GroupFilter).filters !== undefined;
}

export function isIndividual(filter: Filter): filter is IndividualFilter {
  return (filter as IndividualFilter).key !== undefined;
}

export function isRootFilter(filter: Filter): filter is RootFilter {
  if (!isGroup(filter)) {
    return false;
  }
  if (filter.op !== 'OR') {
    return false;
  }
  if (filter.filters.length === 0) {
    return false;
  }
  const firstOrChild = filter.filters[0];
  if (firstOrChild.op !== 'AND') {
    return false;
  }
  return true;
}

export function isMultiValue(
  filter: IndividualFilter
): filter is MultiValueFilter {
  return isMultiOp(filter.op);
}

export function isMultiOp(op: IndividualOp): op is MultiValueOp {
  return _.indexOf(multiValueOps, op) !== -1;
}

export function opAllowsAdditions(op: IndividualOp): boolean {
  return noAdditionOps.indexOf(op) === -1;
}

export function isEmpty(filter: Filter): boolean {
  return isIndividual(filter) && filter.key.name === '';
}

// We don't just use _.isEqual, because we inject view object refs everywhere
// but not always :(
export function filtersAreEqual(
  left: IndividualFilter,
  right: IndividualFilter
) {
  return (
    left.key.section === right.key.section &&
    left.key.name === right.key.name &&
    left.op === right.op &&
    left.value === right.value
  );
}

type QueryPathItem = number | string;

// legacy
export const EMPTY_FILTERS: RootFilter = {
  op: 'OR',
  filters: [
    {
      op: 'AND',
      filters: [],
    },
  ],
};

// !! Important
// Filters have some historical baggage. Currently all filters start with
// from the interface should fit this type
export interface RootFilter {
  op: 'OR';
  filters: [
    {
      op: 'AND';
      filters: Filter[];
    }
  ];
}

export const DEFAULT_FILTERS: Filter = {
  op: 'OR',
  filters: [],
};

export const MATCH_NONE_FILTER: IndividualFilter = {
  key: {section: 'run', name: 'name'},
  op: '=',
  value: '<NONE>',
};

export function match(filter: Filter, run: Run.Run): boolean {
  const matchResult = matchWithDisabled(filter, run);
  if (typeof matchResult === 'boolean') {
    return matchResult;
  } else {
    return true;
  }
}

function notNull<TValue>(value: TValue | null): value is TValue {
  return value !== null;
}

export function matchWithDisabled(
  filter: Filter,
  run: Run.Run
): boolean | null {
  /**
   * (OR, [true, disabled, false]) => true
   * (AND, [true, disabled, false]) => false
   * (OR, [disabled]) => true
   * (AND, [disabled]) => true
   * (OR, []) => true
   * (AND, []) => true
   * (OR, [disabled, disabled]) => true
   * (AND, [disabled, disabled]) => true
   *
   * With Individual filters, it returns bool | null
   * disabled => null
   *
   */
  if (isGroup(filter)) {
    const result = filter.filters.map(f => matchWithDisabled(f, run));
    const booleanResult = result.filter(notNull);
    if (booleanResult.length === 0) {
      // if length of the filters is zero, maybe becaues all the filters
      // are disabled or no filters, we return true
      return true;
    }
    if (filter.op === 'AND') {
      return booleanResult.every(o => o);
    } else {
      return booleanResult.some(o => o);
    }
  } else if (isIndividual(filter)) {
    if (filter.key.name === '') {
      return null;
    }
    // This is bad: we use keys_info to ask the server for runs that
    // contain specific history keys. But we use this client-side match
    // function when we do client-side grouping (run set grouping). We
    // don't have a run's history available here, so I'm just returning
    // true for any keys_info filter. We know this is ok in the couple
    // of cases that Filter.match is actually used. But we should fix
    // this eventually by either only ever filtering on the server, or
    // making sure run has the history data available here and check it.
    // TODO: fix.
    // console.log(filter);
    if (filter.disabled != null && filter.disabled) {
      return null;
    }
    if (filter.key.section === 'keys_info') {
      return true;
    }
    const value = Run.getValue(run, filter.key);
    if (filter.op === '=') {
      if (filter.value === '*') {
        return value != null;
      } else {
        return filter.value === value;
      }
    } else if (filter.op === '!=') {
      if (filter.value === '*') {
        return value === null;
      }
      return filter.value !== value;
    }
    if (filter.value != null) {
      if (isMultiValue(filter) && filter.value.length > 0) {
        if (filter.op === 'IN') {
          return _.indexOf(filter.value, value) !== -1;
        } else if (filter.op === 'NIN') {
          return _.indexOf(filter.value, value) === -1;
        }
      }
      if (value != null) {
        if (filter.op === '<') {
          return value < filter.value!;
        } else if (filter.op === '>') {
          return value > filter.value!;
        } else if (filter.op === '<=') {
          return value <= filter.value!;
        } else if (filter.op === '>=') {
          return value >= filter.value!;
        }
      }
    }
  }
  return false;
}

export function filterRuns(filter: Filter, runs: Run.Run[]) {
  return runs.filter(run => match(filter, run));
}

export function And(filters: Filter[]): Filter {
  // We can merge if we have all individual and AND filters
  // It would be better to implement this by making a good simplify() function and then
  // calling it on {op: 'AND', filters}
  const andFilts = filters.filter(
    f => isIndividual(f) || (isGroup(f) && f.op === 'AND')
  );
  if (andFilts.length === filters.length) {
    return {
      op: 'AND',
      filters: _.flatten(
        andFilts.map(f => (isIndividual(f) ? [f] : f.filters))
      ),
    };
  }
  return {op: 'AND', filters};
}

export function Or(filters: Filter[]): Filter {
  // We can merge if we have all individual and OR filters
  const orFilts = filters.filter(
    f => isIndividual(f) || (isGroup(f) && f.op === 'OR')
  );
  if (orFilts.length === filters.length) {
    return {
      op: 'OR',
      filters: _.flatten(orFilts.map(f => (isIndividual(f) ? [f] : f.filters))),
    };
  }
  return {op: 'OR', filters};
}

export class Update {
  static groupPush<T>(
    owner: T,
    path: QueryPathItem[],
    filter: Filter | Filter[]
  ): T {
    return update(
      owner,
      genUpdate(path, {
        filters: {$push: Array.isArray(filter) ? filter : [filter]},
      })
    );
  }

  static groupRemove<T>(owner: T, path: QueryPathItem[], index: number): T {
    return update(owner, genUpdate(path, {filters: {$splice: [[index, 1]]}}));
  }

  static setFilter<T>(owner: T, path: QueryPathItem[], filter: Filter): T {
    return update(owner, genUpdate(path, {$set: filter}));
  }
}

// Can't build this up as query directly, so we take the Tree type from
// immutability-helper and use it.
type Tree<T> = {[K in keyof T]?: Query<T[K]>};
function genUpdate(path: QueryPathItem[], updateQuery: Query<any>): Query<any> {
  const result: Tree<any> = {};
  let node = result;
  path.forEach(pathItem => {
    node.filters = {[pathItem]: {}};
    node = node.filters[pathItem] as Tree<any>;
  });
  Object.assign(node, updateQuery);
  return result;
}

// This is a centralized place to merge mergefilters with the filters.
// This will be nested a ton, so likely won't be renderable. Should only be used
// as an argument to a graphql query.
export function mergedFilters(filters: Filter, mergeFilters?: Filter): Filter {
  const resultFilters = [filters];
  if (mergeFilters != null) {
    resultFilters.push(mergeFilters);
  }
  return And(resultFilters);
}

///// check* functions and fromJson are used for making sure server data is
// in the format we expect. We convert it to safely typed TypeScript data.

function checkIndividualFilter(filter: any): IndividualFilter | null {
  if (_.indexOf(ops, filter.op) === -1) {
    return null;
  }
  let filterKey: Run.Key | null;
  // We allow both colon-separate string or object formats.
  if (typeof filter.key === 'string') {
    filterKey = Run.keyFromString(filter.key);
  } else if (typeof filter.key === 'object') {
    filterKey = Run.checkKey(filter.key.section, filter.key.name);
  } else {
    return null;
  }
  if (filterKey == null) {
    return null;
  }
  // if (filter.value == null) {
  //   return null;
  // }
  if (isMultiValue(filter)) {
    return {
      key: filterKey,
      op: filter.op,
      value: filter.value,
    };
  } else {
    return {
      key: filterKey,
      op: filter.op,
      value: Run.parseValue(filter.value),
    };
  }
}

function checkGroupFilter(filter: any): GroupFilter | null {
  if (filter.op !== 'AND' && filter.op !== 'OR') {
    return null;
  }
  const filters = checkGroupFilterSet(filter.filters);
  if (!filters) {
    return null;
  }
  return {
    op: filter.op,
    filters,
  };
}

function checkGroupFilterSet(filters: any): Filter[] | null {
  if (!(filters instanceof Array)) {
    return null;
  }
  const result = filters.map(checkFilter);
  if (result.some(o => o == null)) {
    return null;
  }
  // We know none of them are null after the check above.
  return result as Filter[];
}

function checkFilter(filter: any): Filter | null {
  if (filter.op === 'AND' || filter.op === 'OR') {
    return checkGroupFilter(filter);
  } else {
    return checkIndividualFilter(filter);
  }
}

function checkAndFilter(filter: any): Filter | null {
  const parsed = checkFilter(filter);
  if (parsed == null) {
    return null;
  }
  if (parsed.op !== 'AND') {
    return null;
  }
  return parsed;
}

function checkOrOfAndFilter(filter: any): Filter | null {
  const parsed = checkFilter(filter);
  if (parsed == null) {
    return null;
  }
  if (parsed.op !== 'OR') {
    return null;
  }
  if (parsed.filters.length === 0) {
    return null;
  }
  if (parsed.filters[0].op !== 'AND') {
    return null;
  }
  return parsed;
}

export function fromJson(json: any): Filter | null {
  if (json == null) {
    return null;
  }
  return checkFilter(json);
}

export function fromJsonAnd(json: any): Filter | null {
  if (json == null) {
    return null;
  }
  return checkAndFilter(json);
}

export function fromJsonOrOfAnd(json: any): Filter | null {
  if (json == null) {
    return null;
  }
  return checkOrOfAndFilter(json);
}

export function fromOldURL(filterStrings: string[]): Filter | null {
  /* Read filters from the old URL format */
  const result = filterStrings.map(filterString => {
    let parsed;
    try {
      parsed = JSON.parse(filterString);
    } catch (e) {
      return null;
    }
    if (!_.isArray(parsed) || parsed.length !== 3) {
      return null;
    }
    const [keyString, op, valueAny] = parsed;
    const value: Run.Value = valueAny;
    const key = Run.keyFromString(keyString);
    if (key == null || key.section == null || key.name == null) {
      return null;
    }
    return {key, op, value};
  });
  if (result.some(f => f == null)) {
    return null;
  }
  const andFilters = checkGroupFilterSet(result);
  if (andFilters == null) {
    return null;
  }
  return {op: 'OR', filters: [{op: 'AND', filters: andFilters}]};
}

export function fromOldQuery(oldQuery: any): Filter | null {
  // Parses filters stored in the old format. Not super safe.
  if (!_.isArray(oldQuery)) {
    return null;
  }
  const individualFilters: any = oldQuery
    .map((f: any) =>
      f.key && f.key.section && f.key.value && f.op && f.value
        ? {
            key: {section: f.key.section, name: f.key.value},
            op: f.op,
            value: f.value,
          }
        : null
    )
    .filter(o => o);
  return {op: 'OR', filters: [{op: 'AND', filters: individualFilters}]};
}

export function fromURL(filterString: string): Filter | null {
  let result;
  try {
    result = JSON.parse(filterString);
  } catch {
    return null;
  }
  return fromJson(result);
}

export function toURL(filter: Filter): string {
  return JSON.stringify(filter);
}

export function flatIndividuals(filter: Filter): IndividualFilter[] {
  if (isIndividual(filter)) {
    return [filter];
  } else if (isGroup(filter)) {
    return filter.filters.reduce(
      (acc, f) => acc.concat(flatIndividuals(f)),
      [] as IndividualFilter[]
    );
  }
  return [];
}

export function countIndividual(filter: Filter): number {
  return flatIndividuals(filter).length;
}

export function displayIndividualValue(filter: IndividualFilter): string {
  let value: string = '';
  if (isMultiValue(filter)) {
    if (filter.value != null) {
      value = filter.value.join(',');
    } else {
      value = 'null;';
    }
  } else {
    value = Run.displayValue(filter.value);
  }
  return value;
}

function displayIndividual(filter: IndividualFilter): string {
  return (
    Run.displayKey(filter.key) +
    ' ' +
    filter.op +
    ' ' +
    displayIndividualValue(filter)
  );
}

export function summaryString(filter: Filter): string {
  // This just finds all individual filters in the tree and displays
  // them as a comma-separated list. Obviously not fully descriptive,
  // but works for the common case where we have a single AND within
  // an OR
  const filts = flatIndividuals(filter);
  return _.map(filts, displayIndividual).join(', ');
}

export function domValue(
  filter: IndividualFilter
): Run.DomValue | Run.DomValue[] {
  if (isMultiValue(filter)) {
    if (filter.value == null) {
      return [];
    } else {
      return filter.value.map(Run.domValue);
    }
  } else {
    return Run.domValue(filter.value);
  }
}

export function simplifyInner(filter: Filter): Filter | boolean | null {
  // Removes any group filters that are empty.
  // Converts group filters with one child to individual filters
  if (isGroup(filter)) {
    // Simplify children first
    const simplifiedChildren = filter.filters
      .map(simplifyInner)
      .filter(Obj.notEmpty);

    if (simplifiedChildren.length === 0) {
      return null;
    }

    let newChildren: Filter[] = [];
    if (filter.op === 'AND') {
      for (const childFilter of simplifiedChildren) {
        if (childFilter === false) {
          return false;
        } else if (childFilter === true) {
          // x AND true = x
          // skip
        } else if (childFilter.op === filter.op && isGroup(childFilter)) {
          // x AND (y AND z) = x AND y AND z
          newChildren = newChildren.concat(childFilter.filters);
        } else {
          newChildren.push(childFilter);
        }
      }
      if (newChildren.length === 0) {
        return true;
      }
    } else if (filter.op === 'OR') {
      for (const childFilter of simplifiedChildren) {
        if (childFilter === true) {
          // x OR true = true
          return true;
        } else if (childFilter === false) {
          // x OR false = x
          // skip
        } else if (childFilter.op === filter.op && isGroup(childFilter)) {
          // x OR (y OR z) = x OR y OR z
          newChildren = newChildren.concat(childFilter.filters);
        } else {
          newChildren.push(childFilter);
        }
      }
      if (newChildren.length === 0) {
        return false;
      }
    }

    if (newChildren.length === 1) {
      // A group filter with one child is equivalent to the child.
      return newChildren[0];
    }

    return {op: filter.op, filters: newChildren};
  }

  // convert run name filters, used by selection manager to produce
  // true/false
  if (filtersAreEqual(filter, TRUE_FILTER)) {
    return true;
  } else if (filtersAreEqual(filter, FALSE_FILTER)) {
    return false;
  }
  return filter;
}

export function simplify(filter: Filter): Filter | boolean {
  const result = simplifyInner(filter);
  if (result == null) {
    // No filters at all, just match everything
    return true;
  }
  return result;
}

export function simplifiedFiltersToValidFilters(
  filters: Filter | true | false
): Filter {
  if (filters === false) {
    return FALSE_FILTER;
  } else if (filters === true) {
    return TRUE_FILTER;
  }
  return filters;
}

const INDIVIDUAL_OP_TO_MONGO: {[op in IndividualOp]: string} = {
  '=': '', // Here for type completion
  '!=': '$ne',
  '>': '$gt',
  '>=': '$gte',
  '<': '$lt',
  '<=': '$lte',
  IN: '$in',
  NIN: '$nin',
  REGEX: '$regex',
  EXISTS: '$exists',
};
function toMongoOpValue(op: IndividualOp, value: Run.Value | Run.Value[]): any {
  if (op === '=') {
    return value;
  } else {
    return {[INDIVIDUAL_OP_TO_MONGO[op]]: value};
  }
}

function toMongoIndividual(filter: IndividualFilter): any {
  if (filter.key.name === '') {
    return null;
  }
  // It's only valid to compare null to '=' and '!='. We need to support the comparing to null so
  // that we can send down a filter to select the null group (when grouping the table).
  // This also allows the user to specify an '= null' filter. But a '> null' filter will get
  // automatically stripped (these cause a backend sqlalchemy crash) for example.
  // TODO: Make it impossible for the UI to generate filters that don't make sense, like
  // '> null'.
  if (filter.value == null && filter.op !== '=' && filter.op !== '!=') {
    return null;
  }

  // check for disabled filter
  // if disabled flag is undefined by default and it means
  // filter is enabled
  if (filter.disabled != null && filter.disabled) {
    return null;
  }

  if (filter.key.section === 'tags') {
    if (filter.op === 'IN') {
      return {tags: {$in: filter.value}};
    }
    if (filter.value === false) {
      return {
        $or: [{tags: null}, {tags: {$ne: filter.key.name}}],
      };
    } else {
      return {tags: filter.key.name};
    }
  }
  const path = Run.keyToServerPath(filter.key);
  if (path == null) {
    return path;
  }
  return {
    [path]: toMongoOpValue(filter.op, filter.value),
  };
}

const GROUP_OP_TO_MONGO = {
  AND: '$and',
  OR: '$or',
};
export function toMongo(filter: Filter): any {
  if (isIndividual(filter)) {
    return toMongoIndividual(filter);
  } else if (isGroup(filter)) {
    return {
      [GROUP_OP_TO_MONGO[filter.op]]: filter.filters
        .map(toMongo)
        .filter(o => o),
    };
  }
}

export function toString(filter: Filter | true | false): string {
  if (filter === true) {
    return 'true';
  } else if (filter === false) {
    return 'false';
  }
  if (isGroup(filter)) {
    return '(' + filter.filters.map(toString).join(' ' + filter.op + ' ') + ')';
  } else {
    return (
      Run.keyToString(filter.key) +
      ' ' +
      filter.op +
      ' ' +
      (filter.value == null ? 'NULL' : filter.value.toString())
    );
  }
}

export function asGroup(filter: Filter): GroupFilter {
  return {op: 'OR', filters: [filter]};
}

// Filter Factories
export const hideCrashed: IndividualFilter = {
  op: '!=',
  value: 'crashed',
  key: {name: 'state', section: 'run'},
};
export const FILTER_LABEL_HIDE_CRASHED = 'Hide crashed runs';

export function usernameFilter(username: string): IndividualFilter {
  return {
    op: '=',
    value: username,
    key: {name: 'username', section: 'run'},
  };
}
export const FILTER_LABEL_SHOW_MY_WORK = 'Show only my work';

export function runRegexFilter(
  regex: string,
  section: RunKeySection = 'run',
  name: string = 'name'
): IndividualFilter {
  return {
    op: 'REGEX',
    value: regex,
    key: {section, name},
  };
}

export function runNamesFilter(names: string[]): MultiValueFilter {
  return {
    key: {section: 'run', name: 'name'},
    op: 'IN',
    value: names,
  };
}

export const defaultNewRunFilter: Filter = {
  key: {section: 'run', name: 'duration'},
  op: '>=',
  // TODO(volta): Make this value not stack,
  // Currently it only accepts values that have an equivalent.
  value: '5',
  meta: {unit: 'seconds'},
};

// Walk filter tree, applying user's function
export function treeMap(filter: Filter, fn: (f: Filter) => Filter): Filter {
  const applied = fn(filter);
  if (isGroup(applied)) {
    return {...applied, filters: applied.filters.map(f => treeMap(f, fn))};
  }
  return applied;
}

// Walk filter tree, applying user's function to leaf nodes
export function treeForEach(filter: Filter, fn: (f: Filter) => void): void {
  if (isGroup(filter)) {
    filter.filters.forEach(f => treeForEach(f, fn));
  } else {
    fn(filter);
  }
}

export function fixFilter(filter: Filter): Filter {
  return treeMap(filter, f =>
    isIndividual(f) ? {...f, key: Run.fixConfigKey(f.key)} : f
  );
}

export function rootFilterSingle(filter: IndividualFilter): RootFilter {
  return {
    op: 'OR',
    filters: [
      {
        op: 'AND',
        filters: [filter],
      },
    ],
  };
}

export function getAndFilterFromRootFilter(root: RootFilter): AndFilter {
  if (!isRootFilter(root)) {
    throw new Error('filter is not a RootFilter');
  }
  return root.filters[0];
}

export function getFilterListFromRootFilter(root: RootFilter): Filter[] {
  return getAndFilterFromRootFilter(root).filters;
}
